import { currencyToNumber, stripHtmlTags } from 'functions';

import Container from 'Components/Container';
import Footer from 'Components/Footer';
import Layout from 'Components/Layout';
import noimage from 'img/noimage.jpeg';
import client from 'Lib/ApolloClient';
import { OUT_OF_STOCK } from 'Lib/constants/product';
import { CMS_NAME } from 'Lib/constants/settings';
import dynamic from 'next/dynamic';
import ErrorPage from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { SINGLE_PRODUCT_QUERY } from 'Queries/PRODUCT';
import { PRODUCTS } from 'Queries/PRODUCTS';
import { isMobile } from 'react-device-detect';
import isEmpty from 'utils/isEmpty';
import { getProductBrand } from 'utils/productCollectionUtils';

const DesktopDetail = dynamic(() =>
  import('Components/ProductDetail/DesktopDetail')
)

const MobileDetail = dynamic(() =>
  import('Components/ProductDetail/MobileDetail')
)

const RelevantProduct = dynamic(() =>
  import('Components/ProductDetail/RelevantProduct')
)

export default function Product(props) {
  const { product } = props;
  const router = useRouter();

  if (!router.isFallback && isEmpty(product)) {
    return <ErrorPage statusCode={404} />;
  }

  const productBrand = getProductBrand(product.productBrands)

  const ORG_SCHEMA = JSON.stringify({
    "@context": "http://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": product?.image?.sourceUrl || noimage,
    "description": !isEmpty(product.shortDescription) ? stripHtmlTags(product.shortDescription) : stripHtmlTags(product.description.slice(0, 300)),
    "url": `${process.env.SITE_URL}/product/${product.slug}`,
    "brand": {
      "@type": "Brand",
      "name": productBrand.name
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": Math.floor(Math.random() * (500 - 200) + 200)
    },
    "offers": {
      "@type": "Offer",
      "priceValidUntil": "2023/12/31",
      "url": `${process.env.SITE_URL}/product/${product.slug}`,
      "availability": product.stockStatus === OUT_OF_STOCK ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      "priceCurrency": "VND",
      "price": currencyToNumber(product?.onSale ? product?.regularPrice : product?.price),
    }
  })

  return (
    <Layout>
      {product ? (
        <>
          <Head>
            <title>
              {product.name} | {CMS_NAME}
            </title>
            <link
              rel="canonical"
              href={`${process.env.SITE_URL}/product/${product.slug}`}
              key="canonical"
            />

            <meta
              property="og:image"
              content={product?.image?.sourceUrl || noimage}
              key="ogimage"
            />
            <meta
              property="og:url"
              content={`${process.env.SITE_URL}/product/${product.slug}`}
              key="ogurl"
            />
            <meta
              property="og:type"
              content="product"
              key="ogtype"
            />
            <meta
              property="og:title"
              content={product.name}
              key="ogtitle"
            />
            <meta
              property="og:description"
              content={!isEmpty(product.shortDescription) ? stripHtmlTags(product.shortDescription) : stripHtmlTags(product.description.slice(0, 300))}
              key="ogdescription"
            />
            <meta
              name="description"
              content={!isEmpty(product.shortDescription) ? stripHtmlTags(product.shortDescription) : stripHtmlTags(product.description.slice(0, 300))}
            />
            <script type='application/ld+json' dangerouslySetInnerHTML={{
              __html: ORG_SCHEMA
            }} />

          </Head>
          {isMobile ? <MobileDetail product={product} /> : <DesktopDetail product={product} />}
          {/* <div className="lg:hidden">
            <MobileDetail product={product} />
          </div>
          <div className="hidden lg:inline">
            <DesktopDetail product={product} />
          </div> */}
          <Container>
            <RelevantProduct
              productBrand={productBrand}
            />
          </Container>
          <Footer />
        </>
      ) : (
        null
      )}
    </Layout>
  );
}

export async function getStaticProps(context) {
  const {
    params: { slug }
  } = context;

  const { data, loading } = await client.query({
    query: SINGLE_PRODUCT_QUERY,
    variables: { slug },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache'
  });


  if (!data?.product) {
    return {
      redirect: {
        permanent: false,
        destination: "/"
      }
    }
  }

  // console.log()

  return {
    props: {
      product: data?.product || {},
      loading
    }
  };
}

export async function getStaticPaths() {
  const { data } = await client.query({
    query: PRODUCTS
  });

  const pathsData = [];

  data?.products?.nodes &&
    data?.products?.nodes.map((product) => {
      if (!isEmpty(product?.slug)) {
        pathsData.push({ params: { slug: product?.slug } });
      }
    });

  return {
    paths: pathsData,
    fallback: 'blocking'
  };
}

export const fetchCache = 'force-no-store'